import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HardHat, Wrench, Zap, Layers, Paintbrush, LayoutGrid, Wind, TreePine, Utensils, Droplet, Sun, Square,
  DoorOpen, Hammer, Ruler, Building2, ArrowRight, Sparkles, Waves, Trash2, Droplets, Leaf, Camera, Bell,
  KeyRound, Cpu, Flame, Bug, Tv, Sofa, Axe, PlugZap, Thermometer, ClipboardCheck, ShieldCheck, Home,
  Car, DoorClosed, Repeat, Warehouse,
} from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { BRAND, CADENCE_LABELS, SERVICE_GROUPS, ServiceGroup, TRADES, tradeLabel, tradeAccent } from '@/data/constants';

type Icon = React.ComponentType<{ className?: string }>;

/** Trade slug -> icon. Missing keys fall back to the group icon. */
const TRADE_ICONS: Record<string, Icon> = {
  roofing: HardHat, plumbing: Wrench, electrical: Zap, hvac: Wind, flooring: LayoutGrid, painting: Paintbrush,
  solar: Sun, concrete: Layers, landscaping: TreePine, windows: Square, doors: DoorOpen,
  'kitchen-remodel': Utensils, 'bathroom-remodel': Droplet, 'general-construction': Building2,
  framing: Hammer, drywall: Ruler,
  handyman: Wrench, 'house-cleaning': Sparkles, 'deep-cleaning': Sparkles, 'move-out-cleaning': Sparkles,
  'window-cleaning': Square, 'gutter-cleaning': Droplets, 'pressure-washing': Droplets, 'carpet-cleaning': Sparkles,
  'air-duct-cleaning': Wind, 'dryer-vent-cleaning': Wind, 'junk-removal': Trash2, 'furniture-assembly': Sofa,
  'tv-mounting': Tv, 'pool-cleaning': Waves, 'pool-repair': Waves, 'pest-control': Bug,
  'lawn-care': Leaf, 'tree-trimming': TreePine, 'stump-removal': Axe, irrigation: Droplets, 'artificial-turf': Leaf,
  'fence-repair': LayoutGrid, 'gate-repair': DoorClosed,
  'appliance-repair': Wrench, 'water-heater-repair': Thermometer, 'garage-door-repair': Warehouse,
  'ev-charger-installation': Car,
  'property-maintenance': Home, 'rental-turnover': KeyRound, 'hoa-maintenance': Building2, 'property-inspections': ClipboardCheck,
  'security-cameras': Camera, 'alarm-systems': Bell, 'access-control': KeyRound, 'smart-home': Cpu,
  'water-damage': Droplets, 'mold-remediation': ShieldCheck, 'fire-damage': Flame,
};

const GROUP_ICONS: Record<ServiceGroup, Icon> = {
  home: Sparkles, outdoor: Leaf, mechanical: PlugZap, property: Home, safety: ShieldCheck, restoration: Droplets, construction: HardHat,
};

/**
 * Category rail, grouped by service group. GIGGONE is a complete
 * homeowner-services marketplace, so Home Services opens first; Construction
 * is one tab among seven. Every tile is generated from TRADES — a new category
 * appears here, in the filters, the sign-up pickers and the alerts without a
 * second edit. Recurring services carry a small "repeat" badge.
 */
const CategoryStrip: React.FC = () => {
  const { t, path, lang } = useI18n();
  const [group, setGroup] = useState<ServiceGroup>('home');
  const shown = TRADES.filter((tr) => tr.group === group);
  const es = lang === 'es';

  return (
    <section className="bg-white py-10 sm:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
              {es ? 'Todos los servicios para su hogar, en un solo lugar' : 'Every home service, one marketplace'}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {es
                ? `${TRADES.length} categorías: limpieza, albercas, jardín, reparaciones, seguridad, restauración y construcción.`
                : `${TRADES.length} categories — cleaning, pools, lawn, repairs, security, restoration and construction.`}
            </p>
          </div>
          <Link
            to={path('/jobs')}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange transition-colors hover:text-orange-700"
          >
            {t('common.viewAll')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Group tabs — horizontally scrollable on phones. */}
        <div className="-mx-4 mt-5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
          <div role="tablist" aria-label={es ? 'Grupos de servicios' : 'Service groups'} className="flex w-max gap-2">
            {SERVICE_GROUPS.map((g) => {
              const GIcon = GROUP_ICONS[g.key];
              const on = g.key === group;
              const count = TRADES.filter((tr) => tr.group === g.key).length;
              return (
                <button
                  key={g.key}
                  role="tab"
                  type="button"
                  aria-selected={on}
                  onClick={() => setGroup(g.key)}
                  className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 text-sm font-bold transition-colors ${
                    on ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                  }`}
                >
                  <GIcon className="h-4 w-4" />
                  {es ? g.es : g.en}
                  <span className={`rounded-full px-1.5 text-[11px] ${on ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <ul role="tabpanel" className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {shown.map((tr) => {
            const TIcon = TRADE_ICONS[tr.slug] || GROUP_ICONS[tr.group];
            const accent = tradeAccent(tr.slug);
            const recurring = tr.cadence !== 'project';
            return (
              <li key={tr.slug}>
                <Link
                  to={`${path('/jobs')}?trade=${tr.slug}`}
                  className="group relative flex h-full flex-col items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3 py-5 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-card"
                >
                  {recurring && (
                    <span
                      className="absolute right-2 top-2 inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700"
                      title={CADENCE_LABELS[tr.cadence][lang]}
                    >
                      <Repeat className="h-2.5 w-2.5" /> {es ? 'Recurrente' : 'Recurring'}
                    </span>
                  )}
                  <span
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ring-1 transition-transform duration-200 group-hover:scale-110 ${accent.bg} ${accent.fg} ${accent.ring}`}
                  >
                    <TIcon className="h-6 w-6" />
                  </span>
                  <span className="text-sm font-bold leading-snug text-slate-800 group-hover:text-slate-900">
                    {tradeLabel(tr.slug, lang)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>

        <p className="mt-5 text-sm text-slate-500">
          {t('home.tradesContact')}{' '}
          <a href={`mailto:${BRAND.email}`} className="font-semibold text-brand-orange hover:text-orange-700">
            {BRAND.email}
          </a>
        </p>
      </div>
    </section>
  );
};

export default CategoryStrip;
